import {
  Users,
  Calendar,
  Landmark,
  DollarSign,
  GraduationCap,
  Mail,
  Shield,
  MessageSquare,
  FileText,
  Phone,
} from 'lucide-react';

const TEAL = '#14b8a6';
const TEAL_LIGHT = '#2dd4bf';
const EMERALD = '#10b981';

const features = [
  {
    icon: Users,
    title: 'Member Management',
    desc: 'Full member lifecycle from application to renewal. Track demographics, engagement, certifications, and membership tiers with automated onboarding workflows.',
    accent: TEAL,
  },
  {
    icon: Calendar,
    title: 'Events & Registration',
    desc: 'Create events with online registration, ticketing, and check-in. Conferences, galas, workshops, and board meetings all managed in one calendar.',
    accent: TEAL_LIGHT,
  },
  {
    icon: Landmark,
    title: 'Committees & Governance',
    desc: 'Organize boards, committees, and task forces. Track terms, meeting minutes, voting records, and document management for full governance transparency.',
    accent: EMERALD,
  },
  {
    icon: DollarSign,
    title: 'Finance & Dues',
    desc: 'Automated dues invoicing, online payments, financial reporting, and receipt generation. Track revenue by membership type, sponsorships, and events.',
    accent: TEAL,
  },
  {
    icon: GraduationCap,
    title: 'Learning Management',
    desc: 'Built-in LMS for continuing education, certification courses, and professional development. Track completions, issue certificates, and award credits.',
    accent: TEAL_LIGHT,
  },
  {
    icon: Mail,
    title: 'Communications',
    desc: 'Send targeted email and SMS campaigns to segments, committees, or your entire membership. Newsletters, announcements, and automated reminders.',
    accent: EMERALD,
  },
  {
    icon: Shield,
    title: 'Advocacy Tools',
    desc: 'Manage advocacy campaigns, track legislation, organize grassroots action alerts, and measure constituent engagement with elected officials.',
    accent: TEAL,
  },
  {
    icon: MessageSquare,
    title: 'Community Forum',
    desc: 'Give members a place to connect. Discussion boards, Q&A threads, and resource sharing with moderation tools and topic categories.',
    accent: TEAL_LIGHT,
  },
  {
    icon: FileText,
    title: 'Content & CMS',
    desc: 'Publish member-facing content: resource libraries, knowledge bases, policy documents, and member directories with custom access levels.',
    accent: EMERALD,
  },
  {
    icon: Phone,
    title: 'Phone Add-On',
    desc: 'Built-in phone system for member support lines. Call routing, voicemail, and call tracking integrated directly into your member records.',
    accent: TEAL,
  },
];

export function FeaturesSection() {
  return (
    <section id="features" className="relative py-28 sm:py-32">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(10,15,13,0) 0%, rgba(4,47,46,0.15) 50%, rgba(10,15,13,0) 100%)',
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6">
        <div className="mb-16 text-center reveal">
          <span className="mb-4 inline-block rounded-full border border-ams-700/40 bg-ams-950/40 px-3 py-1 text-xs font-medium text-ams-400">
            Features
          </span>
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Everything Your Organization Needs
          </h2>
          <p className="mx-auto max-w-2xl text-zinc-400">
            From member onboarding to financial reporting, every tool purpose-built for associations, HOAs, and chambers of commerce.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div
              key={f.title}
              className="reveal group rounded-2xl border border-ams-800/30 bg-ams-950/20 p-6 backdrop-blur transition hover:border-ams-700/50 hover:bg-ams-950/40"
            >
              <div
                className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ background: `${f.accent}15` }}
              >
                <f.icon className="h-5 w-5" style={{ color: f.accent }} />
              </div>
              <h3 className="mb-2 text-base font-semibold">{f.title}</h3>
              <p className="text-sm leading-relaxed text-zinc-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
